import React, { useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  FaUserAlt,
  FaLock,
  FaArrowRight,
  FaTimes,
  FaExclamationCircle,
  FaShieldAlt,
} from "react-icons/fa";
import CONFIG from "./../../config.js";

// ─────────────────────────────────────────────────────────────────────────────
// Mobile + 4-digit PIN login, OTP-gated only where it needs to be:
//   - first-time verification (new mobile number)
//   - creating the first PIN
//   - recovering/resetting the PIN (via "Forgot PIN" or after 4 wrong tries)
// Normal returning-user login is just mobile + PIN — no OTP.
//
// Steps:
//   "mobile"  → check-mobile decides: has a PIN already → "pin"; otherwise
//               (brand new number, or an existing account that never set a
//               PIN) → request-otp, then → "otp"
//   "otp"     → 6-digit OTP; verify-otp for immediate feedback, then → "pin-set"
//   "pin-set" → create 4-digit PIN + confirm; posts to create-pin (mode
//               "create") or reset-pin (mode "reset" — locked/forgot-PIN path)
//   "pin"     → existing user's normal PIN entry; wrong tries show the
//               remaining-attempts count; the 4th locks it and routes into
//               the OTP → pin-set ("reset") flow automatically
// ─────────────────────────────────────────────────────────────────────────────

const OTP_LENGTH = 6;
const PIN_LENGTH = 4;

function UserLogin() {
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState("mobile"); // mobile | otp | pin-set | pin
  const [pinSetMode, setPinSetMode] = useState("create"); // "create" | "reset" — which endpoint pin-set submits to

  const [mobileNumber, setMobileNumber] = useState("");
  const [otpArray, setOtpArray]   = useState(Array(OTP_LENGTH).fill(""));
  const [pinArray, setPinArray]   = useState(Array(PIN_LENGTH).fill(""));
  const [confirmPinArray, setConfirmPinArray] = useState(Array(PIN_LENGTH).fill(""));

  const [loading, setLoading] = useState(false);
  const [attemptsRemaining, setAttemptsRemaining] = useState(null);
  const [inlineError, setInlineError] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);

  const [popup, setPopup] = useState({ open: false, title: "", message: "", type: "error" });

  const otpRefs         = useRef([]);
  const pinRefs          = useRef([]);
  const confirmPinRefs   = useRef([]);

  // ── Read redirect intent (unchanged from before) ────────────────────────────
  const rawRedirect = location.state?.redirectTo || "";
  const SAFE_FALLBACK = "/";
  const redirectTo = rawRedirect || SAFE_FALLBACK;
  const pendingCartItems = location.state?.cartItems || null;
  const isCheckoutRedirect = Boolean(rawRedirect && rawRedirect !== "/");

  const showPopup = (title, message, type = "error") => setPopup({ open: true, title, message, type });
  const closePopup = () => setPopup({ open: false, title: "", message: "", type: "error" });

  // ── Generic N-digit box input helpers (shared by OTP / PIN / confirm-PIN) ───
  const makeDigitHandlers = (array, setArray, refs, length) => ({
    onChange: (index, value) => {
      const cleanValue = value.replace(/\D/g, "");
      const updated = [...array];
      if (!cleanValue) { updated[index] = ""; setArray(updated); return; }
      updated[index] = cleanValue.slice(-1);
      setArray(updated);
      if (index < length - 1) refs.current[index + 1]?.focus();
    },
    onKeyDown: (index, e) => {
      if (e.key === "Backspace") {
        if (array[index]) {
          const updated = [...array]; updated[index] = ""; setArray(updated);
        } else if (index > 0) {
          refs.current[index - 1]?.focus();
        }
      }
      if (e.key === "ArrowLeft"  && index > 0)          refs.current[index - 1]?.focus();
      if (e.key === "ArrowRight" && index < length - 1) refs.current[index + 1]?.focus();
    },
    onPaste: (e) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
      if (!pasted) return;
      const updated = Array(length).fill("");
      pasted.split("").forEach((d, i) => { updated[i] = d; });
      setArray(updated);
      refs.current[Math.min(pasted.length, length - 1)]?.focus();
    },
  });

  const otpHandlers         = makeDigitHandlers(otpArray, setOtpArray, otpRefs, OTP_LENGTH);
  const pinHandlers         = makeDigitHandlers(pinArray, setPinArray, pinRefs, PIN_LENGTH);
  const confirmPinHandlers  = makeDigitHandlers(confirmPinArray, setConfirmPinArray, confirmPinRefs, PIN_LENGTH);

  const handleMobileChange = (e) => setMobileNumber(e.target.value.replace(/\D/g, "").slice(0, 10));

  // ── Shared post-login side effects (same as the old single-step flow) ──────
  const mergePendingCart = () => {
    if (!pendingCartItems || pendingCartItems.length === 0) return;
    try {
      const existing = JSON.parse(localStorage.getItem("cartItems") || "[]");
      const merged = [...existing];
      pendingCartItems.forEach((incoming) => {
        const idx = merged.findIndex((m) => String(m.id) === String(incoming.id));
        if (idx > -1) merged[idx].qty = Number(merged[idx].qty || 1) + Number(incoming.qty || 1);
        else merged.push(incoming);
      });
      localStorage.setItem("cartItems", JSON.stringify(merged));
      window.dispatchEvent(new Event("cart-updated"));
    } catch (err) { console.error("Failed to merge pending cart:", err); }
  };

  const completeLogin = (data, mobile) => {
    const userData = {
      id: data.user_id || "",
      name: data.user_name || "",
      full_name: data.user_name || "",
      mobile: data.mobile_number || mobile,
    };
    const authToken = data.token || data.access_token || data.auth_token || "";

    localStorage.setItem("currentUser", JSON.stringify(userData));
    if (authToken) localStorage.setItem("authToken", authToken);

    sessionStorage.setItem("isUserLoggedIn", "true");
    sessionStorage.setItem("user_id", userData.id);
    sessionStorage.setItem("userName", userData.name);
    sessionStorage.setItem("mobile", userData.mobile);

    mergePendingCart();
    localStorage.removeItem("postLoginRedirect");
    window.dispatchEvent(new Event("auth-updated"));

    const finalCartItems = (() => {
      try { return JSON.parse(localStorage.getItem("cartItems") || "[]"); } catch { return []; }
    })();

    navigate(redirectTo, {
      state: {
        user_id: userData.id, user_name: userData.name, mobile_number: userData.mobile,
        cartItems: finalCartItems,
      },
      replace: true,
    });
  };

  const api = async (path, body) => {
    const res = await fetch(`${CONFIG.API_BASE_URL}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    });
    const text = await res.text();
    let data = {};
    try { data = text ? JSON.parse(text) : {}; } catch { /* leave {} */ }
    return { ok: res.ok, status: res.status, data };
  };

  // ── Step 1: mobile number ───────────────────────────────────────────────────
  const handleMobileSubmit = async (e) => {
    e.preventDefault();
    setInlineError("");
    if (mobileNumber.length !== 10) {
      showPopup("Invalid Mobile Number", "Please enter a valid 10-digit mobile number.");
      return;
    }
    try {
      setLoading(true);
      const check = await api("/api/user/check-mobile/", { mobile: mobileNumber });
      if (!check.ok) {
        showPopup("Error", check.data?.error || "Could not verify mobile number. Please try again.");
        return;
      }
      if (check.data.exists && check.data.has_pin) {
        setPinArray(Array(PIN_LENGTH).fill(""));
        setAttemptsRemaining(null);
        setStep("pin");
        return;
      }
      // New number, or an existing account that never set a PIN — same path.
      const otpRes = await api("/api/user/request-otp/", { mobile: mobileNumber });
      if (!otpRes.ok) {
        showPopup("Error", otpRes.data?.error || "Could not send OTP. Please try again.");
        return;
      }
      setPinSetMode("create");
      setOtpArray(Array(OTP_LENGTH).fill(""));
      setOtpVerified(false);
      setStep("otp");
    } catch (err) {
      console.error(err);
      showPopup("Server Error", "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── "Forgot / Reset PIN" from the PIN screen ────────────────────────────────
  const handleForgotPin = async () => {
    setInlineError("");
    try {
      setLoading(true);
      const otpRes = await api("/api/user/request-otp/", { mobile: mobileNumber });
      if (!otpRes.ok) {
        showPopup("Error", otpRes.data?.error || "Could not send OTP. Please try again.");
        return;
      }
      setPinSetMode("reset");
      setOtpArray(Array(OTP_LENGTH).fill(""));
      setOtpVerified(false);
      setStep("otp");
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: OTP ──────────────────────────────────────────────────────────────
  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    const otp = otpArray.join("");
    if (otp.length !== OTP_LENGTH) {
      showPopup("Invalid OTP", `Please enter the ${OTP_LENGTH}-digit OTP.`);
      return;
    }
    try {
      setLoading(true);
      const res = await api("/api/user/verify-otp/", { mobile: mobileNumber, otp });
      if (!res.ok) {
        showPopup("Incorrect OTP", res.data?.error === "Invalid OTP"
          ? "The OTP you entered is incorrect."
          : (res.data?.error || "OTP expired. Please request a new OTP."));
        return;
      }
      setOtpVerified(true);
      setPinArray(Array(PIN_LENGTH).fill(""));
      setConfirmPinArray(Array(PIN_LENGTH).fill(""));
      setStep("pin-set");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      setLoading(true);
      await api("/api/user/request-otp/", { mobile: mobileNumber });
      showPopup("OTP Sent", "A new OTP has been sent to your mobile number.", "info");
    } finally {
      setLoading(false);
    }
  };

  // ── Step 3: create / confirm new PIN ────────────────────────────────────────
  const handlePinSetSubmit = async (e) => {
    e.preventDefault();
    const pin = pinArray.join("");
    const confirmPin = confirmPinArray.join("");
    if (pin.length !== PIN_LENGTH) {
      showPopup("Invalid PIN", `Please enter a ${PIN_LENGTH}-digit PIN.`);
      return;
    }
    if (pin !== confirmPin) {
      showPopup("PIN Mismatch", "PIN and confirmation do not match.");
      return;
    }
    const endpoint = pinSetMode === "reset" ? "/api/user/reset-pin/" : "/api/user/create-pin/";
    try {
      setLoading(true);
      const res = await api(endpoint, {
        mobile: mobileNumber, otp: otpArray.join(""), pin, confirm_pin: confirmPin,
      });
      if (!res.ok) {
        showPopup("Could Not Save PIN", res.data?.error || "Please try again.");
        return;
      }
      completeLogin(res.data, mobileNumber);
    } finally {
      setLoading(false);
    }
  };

  // ── Step 4: existing user — normal PIN login ────────────────────────────────
  const handlePinLoginSubmit = async (e) => {
    e.preventDefault();
    setInlineError("");
    const pin = pinArray.join("");
    if (pin.length !== PIN_LENGTH) {
      showPopup("Invalid PIN", `Please enter your ${PIN_LENGTH}-digit PIN.`);
      return;
    }
    try {
      setLoading(true);
      const res = await api("/api/user/login-pin/", { mobile: mobileNumber, pin });
      if (res.ok) {
        completeLogin(res.data, mobileNumber);
        return;
      }
      if (res.data?.error === "pin_locked") {
        // 4th wrong attempt (or already locked) — force straight into OTP recovery.
        showPopup(
          "PIN Locked",
          "Your PIN has been temporarily locked. Verify your mobile number with OTP to create a new PIN.",
          "error"
        );
        const otpRes = await api("/api/user/request-otp/", { mobile: mobileNumber });
        if (otpRes.ok) {
          setPinSetMode("reset");
          setOtpArray(Array(OTP_LENGTH).fill(""));
          setOtpVerified(false);
          setStep("otp");
        }
        return;
      }
      if (res.data?.error === "no_pin") {
        // Defensive — check-mobile already filters this, but handle it if the
        // account's PIN state changed between that check and this submit.
        const otpRes = await api("/api/user/request-otp/", { mobile: mobileNumber });
        if (otpRes.ok) {
          setPinSetMode("create");
          setOtpArray(Array(OTP_LENGTH).fill(""));
          setOtpVerified(false);
          setStep("otp");
        }
        return;
      }
      // wrong_pin
      setPinArray(Array(PIN_LENGTH).fill(""));
      pinRefs.current[0]?.focus();
      setAttemptsRemaining(typeof res.data?.attempts_remaining === "number" ? res.data.attempts_remaining : null);
      setInlineError(res.data?.message || "Incorrect PIN.");
    } catch (err) {
      console.error(err);
      showPopup("Server Error", "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChangeMobile = () => {
    setStep("mobile");
    setInlineError("");
    setAttemptsRemaining(null);
    setOtpArray(Array(OTP_LENGTH).fill(""));
    setPinArray(Array(PIN_LENGTH).fill(""));
    setConfirmPinArray(Array(PIN_LENGTH).fill(""));
  };

  // ── Styles (unchanged visual language from the original single-step page) ──
  const styles = {
    page: {
      minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "center",
      background: "radial-gradient(circle at top left, #3b82f6 0%, #1d4ed8 35%, #0f172a 100%)",
      padding: "24px", fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", position: "relative",
    },
    card: {
      width: "100%", maxWidth: "440px", background: "rgba(255,255,255,0.96)", borderRadius: "24px",
      padding: "34px 28px 26px", boxShadow: "0 22px 60px rgba(15, 23, 42, 0.28)",
      border: "1px solid rgba(255,255,255,0.45)", backdropFilter: "blur(10px)",
    },
    headerWrap: { textAlign: "center", marginBottom: "24px" },
    badge: {
      display: "inline-block", padding: "6px 12px", borderRadius: "999px", background: "#dbeafe",
      color: "#1d4ed8", fontSize: "12px", fontWeight: "700", marginBottom: "14px",
    },
    heading: { margin: "0 0 8px 0", fontSize: "2rem", fontWeight: "800", color: "#0f172a", letterSpacing: "-0.4px" },
    subText: { margin: 0, fontSize: "14px", color: "#64748b", lineHeight: "1.6" },
    redirectBanner: {
      marginBottom: "18px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "14px",
      padding: "12px 14px", color: "#1d4ed8", fontSize: "13px", fontWeight: "600", lineHeight: "1.6", textAlign: "center",
    },
    formGroup: { marginBottom: "20px" },
    label: {
      display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px",
      fontSize: "14px", fontWeight: "700", color: "#334155",
    },
    input: {
      width: "100%", height: "56px", padding: "0 16px", borderRadius: "14px", border: "1px solid #cbd5e1",
      background: "#f8fafc", fontSize: "16px", color: "#0f172a", outline: "none", boxSizing: "border-box",
    },
    digitGrid: (length) => ({
      display: "grid", gridTemplateColumns: `repeat(${length}, 1fr)`, gap: "10px",
    }),
    digitBox: {
      height: "58px", width: "100%", textAlign: "center", fontSize: "22px", fontWeight: "800",
      borderRadius: "14px", border: "1px solid #cbd5e1", background: "#f8fafc", color: "#0f172a",
      outline: "none", boxSizing: "border-box",
    },
    helper: { fontSize: "12px", color: "#64748b", margin: "10px 0 0" },
    inlineErrorBox: {
      marginBottom: "18px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "14px",
      padding: "12px 14px", color: "#b91c1c", fontSize: "13px", fontWeight: "600", textAlign: "center",
    },
    button: {
      width: "100%", height: "58px", border: "none", borderRadius: "16px",
      background: loading ? "#93c5fd" : "linear-gradient(135deg, #2563eb, #1d4ed8)",
      color: "#fff", fontSize: "17px", fontWeight: "800", cursor: loading ? "not-allowed" : "pointer",
      display: "flex", alignItems: "center", justifyContent: "center", gap: "10px",
      boxShadow: "0 14px 28px rgba(37, 99, 235, 0.28)", marginTop: "6px",
    },
    linkBtn: {
      display: "block", width: "100%", textAlign: "center", marginTop: "14px", background: "none",
      border: "none", color: "#2563eb", fontWeight: "700", fontSize: "13.5px", cursor: "pointer",
    },
    footerText: { textAlign: "center", marginTop: "20px", marginBottom: 0, fontSize: "14px", color: "#475569" },
    link: { color: "#2563eb", fontWeight: "700", textDecoration: "none" },
    modalOverlay: {
      position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.55)", display: "flex",
      alignItems: "center", justifyContent: "center", padding: "20px", zIndex: 9999,
    },
    modalBox: {
      width: "100%", maxWidth: "380px", background: "#fff", borderRadius: "22px", padding: "24px 22px 20px",
      boxShadow: "0 20px 50px rgba(0,0,0,0.22)", position: "relative", textAlign: "center",
    },
    modalClose: {
      position: "absolute", top: "12px", right: "12px", width: "36px", height: "36px", border: "none",
      borderRadius: "50%", background: "#f1f5f9", color: "#334155", display: "flex", alignItems: "center",
      justifyContent: "center", cursor: "pointer", fontSize: "14px",
    },
    modalIcon: {
      width: "64px", height: "64px", borderRadius: "50%", margin: "0 auto 14px", display: "flex",
      alignItems: "center", justifyContent: "center", background: "#fee2e2", color: "#dc2626", fontSize: "28px",
    },
    modalTitle: { margin: "0 0 8px 0", fontSize: "1.3rem", fontWeight: "800", color: "#0f172a" },
    modalMessage: { margin: "0 0 18px 0", fontSize: "14px", color: "#475569", lineHeight: "1.7" },
    modalButton: {
      width: "100%", height: "48px", border: "none", borderRadius: "14px",
      background: "linear-gradient(135deg, #2563eb, #1d4ed8)", color: "#fff", fontSize: "15px",
      fontWeight: "700", cursor: "pointer",
    },
  };

  const renderDigitRow = (array, handlers, refs, length, autoFocusFirst) => (
    <div style={styles.digitGrid(length)}>
      {array.map((digit, index) => (
        <input
          key={index}
          ref={(el) => (refs.current[index] = el)}
          type="password"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          autoFocus={autoFocusFirst && index === 0}
          value={digit}
          onChange={(e) => handlers.onChange(index, e.target.value)}
          onKeyDown={(e) => handlers.onKeyDown(index, e)}
          onPaste={handlers.onPaste}
          required
          style={styles.digitBox}
        />
      ))}
    </div>
  );

  return (
    <>
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.headerWrap}>
            <span style={styles.badge}>Secure Access</span>
            <h2 style={styles.heading}>
              {step === "mobile"  && "User Login"}
              {step === "otp"     && "Verify OTP"}
              {step === "pin-set" && (pinSetMode === "reset" ? "Set New PIN" : "Create Your PIN")}
              {step === "pin"     && "Enter Your PIN"}
            </h2>
            <p style={styles.subText}>
              {step === "mobile"  && "Sign in with your registered mobile number."}
              {step === "otp"     && `Enter the ${OTP_LENGTH}-digit code sent to ${mobileNumber}.`}
              {step === "pin-set" && `Choose a ${PIN_LENGTH}-digit PIN you'll use to log in next time.`}
              {step === "pin"     && `Enter your ${PIN_LENGTH}-digit PIN for ${mobileNumber}.`}
            </p>
          </div>

          {isCheckoutRedirect && step === "mobile" && (
            <div style={styles.redirectBanner}>
              🛒 Please log in to continue.
              {pendingCartItems?.length > 0 &&
                ` Your ${pendingCartItems.length} item${pendingCartItems.length > 1 ? "s" : ""} will be saved.`}
            </div>
          )}

          {inlineError && step === "pin" && (
            <div style={styles.inlineErrorBox}>
              {inlineError}
              {attemptsRemaining != null && (
                <div style={{ marginTop: 4 }}>
                  {attemptsRemaining} attempt{attemptsRemaining !== 1 ? "s" : ""} remaining.
                </div>
              )}
            </div>
          )}

          {/* ── Step: mobile number ─────────────────────────────────────────── */}
          {step === "mobile" && (
            <form onSubmit={handleMobileSubmit}>
              <div style={styles.formGroup}>
                <label style={styles.label}><FaUserAlt /> Mobile Number</label>
                <input
                  type="text" value={mobileNumber} onChange={handleMobileChange}
                  placeholder="Enter 10-digit mobile number" maxLength={10} inputMode="numeric"
                  required autoFocus style={styles.input}
                />
              </div>
              <button type="submit" style={styles.button} disabled={loading}>
                {loading ? "Checking…" : "Continue"} {!loading && <FaArrowRight />}
              </button>
              <p style={styles.footerText}>
                New here? Just enter your mobile number above — we'll verify it and set up your PIN.
              </p>
            </form>
          )}

          {/* ── Step: OTP ────────────────────────────────────────────────────── */}
          {step === "otp" && (
            <form onSubmit={handleOtpSubmit}>
              <div style={styles.formGroup}>
                <label style={styles.label}><FaShieldAlt /> {OTP_LENGTH}-Digit OTP</label>
                {renderDigitRow(otpArray, otpHandlers, otpRefs, OTP_LENGTH, true)}
                <p style={styles.helper}>Enter one digit in each box.</p>
              </div>
              <button type="submit" style={styles.button} disabled={loading}>
                {loading ? "Verifying…" : "Verify OTP"} {!loading && <FaArrowRight />}
              </button>
              <button type="button" style={styles.linkBtn} onClick={handleResendOtp} disabled={loading}>
                Resend OTP
              </button>
              <button type="button" style={styles.linkBtn} onClick={handleChangeMobile} disabled={loading}>
                ← Change mobile number
              </button>
            </form>
          )}

          {/* ── Step: create / reset PIN ─────────────────────────────────────── */}
          {step === "pin-set" && (
            <form onSubmit={handlePinSetSubmit}>
              <div style={styles.formGroup}>
                <label style={styles.label}><FaLock /> Create {PIN_LENGTH}-Digit PIN</label>
                {renderDigitRow(pinArray, pinHandlers, pinRefs, PIN_LENGTH, true)}
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}><FaLock /> Confirm PIN</label>
                {renderDigitRow(confirmPinArray, confirmPinHandlers, confirmPinRefs, PIN_LENGTH, false)}
              </div>
              <button type="submit" style={styles.button} disabled={loading}>
                {loading ? "Saving…" : "Continue"} {!loading && <FaArrowRight />}
              </button>
            </form>
          )}

          {/* ── Step: existing user PIN login ────────────────────────────────── */}
          {step === "pin" && (
            <form onSubmit={handlePinLoginSubmit}>
              <div style={styles.formGroup}>
                <label style={styles.label}><FaLock /> {PIN_LENGTH}-Digit PIN</label>
                {renderDigitRow(pinArray, pinHandlers, pinRefs, PIN_LENGTH, true)}
              </div>
              <button type="submit" style={styles.button} disabled={loading}>
                {loading ? "Logging in…" : "Login"} {!loading && <FaArrowRight />}
              </button>
              <button type="button" style={styles.linkBtn} onClick={handleForgotPin} disabled={loading}>
                Forgot / Reset PIN
              </button>
              <button type="button" style={styles.linkBtn} onClick={handleChangeMobile} disabled={loading}>
                ← Use a different mobile number
              </button>
            </form>
          )}

          {step === "mobile" && (
            <p style={styles.footerText}>
              Prefer the old password login?{" "}
              <Link to="/user/user-signup" style={styles.link}>Sign up here</Link>
            </p>
          )}
        </div>
      </div>

      {popup.open && (
        <div style={styles.modalOverlay} onClick={closePopup}>
          <div style={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <button type="button" style={styles.modalClose} onClick={closePopup}><FaTimes /></button>
            <div style={styles.modalIcon}><FaExclamationCircle /></div>
            <h3 style={styles.modalTitle}>{popup.title}</h3>
            <p style={styles.modalMessage}>{popup.message}</p>
            <button type="button" style={styles.modalButton} onClick={closePopup}>OK</button>
          </div>
        </div>
      )}
    </>
  );
}

export default UserLogin;
